"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { ExternalLink } from "lucide-react"

interface InlineCitationProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode
}

interface InlineCitationTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode
}

interface InlineCitationCardTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  sources?: string[]
  className?: string
}

interface InlineCitationCardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface InlineCitationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface InlineCitationCarouselProps extends React.ComponentProps<typeof Carousel> {
  children: React.ReactNode
}

interface InlineCitationCarouselContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface InlineCitationCarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface InlineCitationCarouselHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface InlineCitationCarouselIndexProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

interface InlineCitationSourceProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  url?: string
  description?: string
}

interface InlineCitationQuoteProps extends React.HTMLAttributes<HTMLQuoteElement> {
  children: React.ReactNode
}

const InlineCitation = React.forwardRef<HTMLSpanElement, InlineCitationProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn("inline-flex items-center gap-1", className)}
        {...props}
      >
        {children}
      </span>
    )
  }
)
InlineCitation.displayName = "InlineCitation"

const InlineCitationText = React.forwardRef<HTMLSpanElement, InlineCitationTextProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn("", className)}
        {...props}
      >
        {children}
      </span>
    )
  }
)
InlineCitationText.displayName = "InlineCitationText"

const InlineCitationCard = React.forwardRef<HTMLDivElement, InlineCitationCardProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center", className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)
InlineCitationCard.displayName = "InlineCitationCard"

const InlineCitationCardTrigger = React.forwardRef<HTMLButtonElement, InlineCitationCardTriggerProps>(
  ({ sources = [], className, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false)

    if (!sources || sources.length === 0) {
      return null
    }

    // Extract hostname from URL for display
    const getHostname = (url: string) => {
      try {
        return new URL(url).hostname.replace('www.', '')
      } catch {
        return url
      }
    }

    const firstSource = sources[0]
    const hostname = getHostname(firstSource)

    return (
      <TooltipProvider>
        <Tooltip open={isOpen} onOpenChange={setIsOpen}>
          <TooltipTrigger asChild>
            <button
              ref={ref}
              className={cn(
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium",
                "bg-primary/10 text-primary hover:bg-primary/20",
                "border border-primary/20",
                "transition-colors cursor-pointer",
                className
              )}
              onClick={(e) => {
                e.preventDefault()
                setIsOpen(!isOpen)
              }}
              {...props}
            >
              <span>{sources.length}</span>
              <ExternalLink className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="start"
            className="max-w-sm p-0 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-lg z-50"
            onPointerDownOutside={(e) => {
              e.preventDefault()
            }}
          >
            {props.children}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
)
InlineCitationCardTrigger.displayName = "InlineCitationCardTrigger"

const InlineCitationCardBody = React.forwardRef<HTMLDivElement, InlineCitationCardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("p-0", className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)
InlineCitationCardBody.displayName = "InlineCitationCardBody"

const InlineCitationCarousel = React.forwardRef<HTMLDivElement, InlineCitationCarouselProps>(
  ({ className, children, ...props }, ref) => {
    const [api, setApi] = React.useState<any>(null)

    React.useEffect(() => {
      if (!api) return

      const updateIndex = () => {
        const current = api.selectedScrollSnap() + 1
        const total = api.scrollSnapList().length
        // Dispatch custom event for InlineCitationCarouselIndex to listen to
        window.dispatchEvent(new CustomEvent('citation-index-update', {
          detail: { current, total }
        }))
      }

      updateIndex()
      api.on('select', updateIndex)
      api.on('reInit', updateIndex)

      return () => {
        api?.off('select', updateIndex)
        api?.off('reInit', updateIndex)
      }
    }, [api])

    return (
      <Carousel
        ref={ref}
        setApi={setApi}
        className={cn("w-full", className)}
        {...props}
      >
        {children}
      </Carousel>
    )
  }
)
InlineCitationCarousel.displayName = "InlineCitationCarousel"

const InlineCitationCarouselContent = React.forwardRef<HTMLDivElement, InlineCitationCarouselContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <CarouselContent
        ref={ref}
        className={cn("p-3", className)}
        {...props}
      >
        {children}
      </CarouselContent>
    )
  }
)
InlineCitationCarouselContent.displayName = "InlineCitationCarouselContent"

const InlineCitationCarouselItem = React.forwardRef<HTMLDivElement, InlineCitationCarouselItemProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <CarouselItem
        ref={ref}
        className={cn("", className)}
        {...props}
      >
        {children}
      </CarouselItem>
    )
  }
)
InlineCitationCarouselItem.displayName = "InlineCitationCarouselItem"

const InlineCitationCarouselHeader = React.forwardRef<HTMLDivElement, InlineCitationCarouselHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center justify-between px-3 pt-3 pb-2 border-b border-neutral-200 dark:border-neutral-800", className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)
InlineCitationCarouselHeader.displayName = "InlineCitationCarouselHeader"

const InlineCitationCarouselIndex = React.forwardRef<HTMLDivElement, InlineCitationCarouselIndexProps>(
  ({ className, children, ...props }, ref) => {
    // Get current and count from parent InlineCitationCarousel via context
    const [indexData, setIndexData] = React.useState({ current: 1, total: 1 })
    
    React.useEffect(() => {
      // Listen for index updates from parent
      const handleIndexUpdate = (e: CustomEvent) => {
        setIndexData(e.detail)
      }
      window.addEventListener('citation-index-update', handleIndexUpdate as EventListener)
      return () => {
        window.removeEventListener('citation-index-update', handleIndexUpdate as EventListener)
      }
    }, [])

    return (
      <div
        ref={ref}
        className={cn("text-xs text-neutral-500 dark:text-neutral-400", className)}
        {...props}
      >
        {children || (indexData.total > 0 ? `${indexData.current} / ${indexData.total}` : '')}
      </div>
    )
  }
)
InlineCitationCarouselIndex.displayName = "InlineCitationCarouselIndex"

const InlineCitationCarouselPrev = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof CarouselPrevious>>(
  ({ className, ...props }, ref) => {
    return (
      <CarouselPrevious
        ref={ref}
        className={cn("h-6 w-6 relative left-0 top-0 translate-x-0 translate-y-0", className)}
        {...props}
      />
    )
  }
)
InlineCitationCarouselPrev.displayName = "InlineCitationCarouselPrev"

const InlineCitationCarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof CarouselNext>>(
  ({ className, ...props }, ref) => {
    return (
      <CarouselNext
        ref={ref}
        className={cn("h-6 w-6 relative right-0 top-0 translate-x-0 translate-y-0", className)}
        {...props}
      />
    )
  }
)
InlineCitationCarouselNext.displayName = "InlineCitationCarouselNext"

const InlineCitationSource = React.forwardRef<HTMLDivElement, InlineCitationSourceProps>(
  ({ title, url, description, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("space-y-2", className)}
        {...props}
      >
        {title && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm font-medium text-primary hover:underline break-all"
          >
            {title}
          </a>
        )}
        {url && !title && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-primary hover:underline font-medium break-all"
          >
            {url}
          </a>
        )}
        {description && (
          <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
            {description}
          </p>
        )}
      </div>
    )
  }
)
InlineCitationSource.displayName = "InlineCitationSource"

const InlineCitationQuote = React.forwardRef<HTMLQuoteElement, InlineCitationQuoteProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <blockquote
        ref={ref}
        className={cn(
          "mt-2 pl-3 border-l-2 border-primary/30 text-xs text-neutral-600 dark:text-neutral-400 italic",
          className
        )}
        {...props}
      >
        {children}
      </blockquote>
    )
  }
)
InlineCitationQuote.displayName = "InlineCitationQuote"

export {
  InlineCitation,
  InlineCitationText,
  InlineCitationCard,
  InlineCitationCardTrigger,
  InlineCitationCardBody,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselPrev,
  InlineCitationCarouselNext,
  InlineCitationSource,
  InlineCitationQuote,
}
